/**
 * Formula Evaluator for Calculated Form Fields
 * 
 * Supports:
 * - Basic arithmetic: +, -, *, /, %
 * - Comparison: <, >, <=, >=, ==, !=
 * - Functions: IF, MAX, MIN, ABS
 * - Field references by label
 */

export interface FormulaContext {
  fieldValues: Record<string, any>;
  fields: Array<{ label: string; type: string; itemReferenceValues?: Record<string, any> }>;
}

/**
 * Evaluates a formula expression
 * @param formula - The formula string (e.g., "Par Level - Current Inventory")
 * @param context - The context containing field values and field definitions
 * @returns The evaluated result
 */
export function evaluateFormula(formula: string, context: FormulaContext): any {
  if (!formula || formula.trim() === '') {
    return null;
  }

  try {
    // Replace field references with actual values
    let expression = replaceFieldReferences(formula, context);
    
    // Evaluate the expression
    const result = evaluateExpression(expression);
    
    return result;
  } catch (error: any) {
    console.error('Formula evaluation error:', error);
    throw new Error(`Formula error: ${error.message}`);
  }
}

/**
 * Replaces field labels in formula with their actual values
 */
function replaceFieldReferences(formula: string, context: FormulaContext): string {
  let expression = formula;
  
  // Sort fields by length (longest first) to avoid partial matches
  // e.g., "Req Stock level" should be replaced before "Stock level"
  const sortedFields = [...context.fields].sort((a, b) => {
    const lenA = a.label?.length || 0;
    const lenB = b.label?.length || 0;
    return lenB - lenA; // Descending order
  });
  
  // Replace field references (field labels in quotes or as identifiers)
  sortedFields.forEach(field => {
    if (!field.label) return;
    
    const fieldValue = getFieldValue(field.label, context);
    const fieldLabel = field.label;
    
    // Convert value to string representation
    let valueStr: string;
    if (fieldValue === null || fieldValue === undefined || fieldValue === '') {
      valueStr = '0'; // Default to 0 for empty values in calculations
    } else if (typeof fieldValue === 'number') {
      valueStr = String(fieldValue);
    } else if (typeof fieldValue === 'string') {
      // Try to parse as number, if it's a quoted string in formula, keep as string
      const numValue = parseFloat(fieldValue);
      if (!isNaN(numValue) && fieldValue.trim() !== '') {
        valueStr = String(numValue);
      } else {
        // String value - wrap in quotes for comparison
        valueStr = `"${fieldValue.replace(/"/g, '\\"')}"`;
      }
    } else if (typeof fieldValue === 'boolean') {
      valueStr = fieldValue ? 'true' : 'false';
    } else {
      valueStr = String(fieldValue);
    }
    
    // Create regex pattern that matches the entire field label
    // For labels with spaces, we need to match the whole label as a unit
    // Ensure we're not matching part of a larger word
    const escapedLabel = escapeRegex(fieldLabel);
    
    // Pattern: Match the label exactly, ensuring it's:
    // - At the start of string OR preceded by a non-word character (space, operator, etc.)
    // - At the end of string OR followed by a non-word character
    // Use capturing group to preserve what comes before (for proper replacement)
    // This handles labels with spaces correctly (spaces are non-word characters)
    const regex = new RegExp(`(^|[^\\w])${escapedLabel}(?=[^\\w]|$)`, 'g');
    
    expression = expression.replace(regex, (match, prefix) => {
      // prefix is either empty string (if at start) or a non-word character
      // We want to keep the prefix and replace just the label
      return prefix + valueStr;
    });
  });
  
  return expression;
}

/**
 * Gets the value for a field label
 */
function getFieldValue(fieldLabel: string, context: FormulaContext): any {
  // Check fieldValues first
  if (context.fieldValues[fieldLabel] !== undefined) {
    return context.fieldValues[fieldLabel];
  }
  
  return null;
}

/**
 * Evaluates a mathematical/logical expression
 * Supports: arithmetic, comparison, IF, MAX, MIN, ABS
 */
function evaluateExpression(expression: string): any {
  // Handle string comparisons and IF statements first
  expression = handleIFStatements(expression);
  expression = handleFunctions(expression);
  
  // Handle comparisons (for boolean results)
  if (expression.includes('<=') || expression.includes('>=') || 
      expression.includes('==') || expression.includes('!=')) {
    return evaluateComparison(expression);
  }
  if (expression.includes('<') || expression.includes('>')) {
    return evaluateComparison(expression);
  }
  
  // Handle arithmetic operations
  return evaluateArithmetic(expression);
}

/**
 * Handles IF(condition, trueValue, falseValue) statements
 */
function handleIFStatements(expression: string): string {
  // Match IF statements: IF(condition, trueValue, falseValue)
  const ifRegex = /IF\s*\(\s*([^,]+)\s*,\s*([^,]+)\s*,\s*([^)]+)\s*\)/gi;
  
  return expression.replace(ifRegex, (match, condition, trueValue, falseValue) => {
    try {
      const conditionResult = evaluateComparison(condition.trim());
      return conditionResult ? trueValue.trim() : falseValue.trim();
    } catch (error) {
      return match; // Return original if evaluation fails
    }
  });
}

/**
 * Handles function calls: MAX, MIN, ABS
 */
function handleFunctions(expression: string): string {
  // MAX(a, b, ...)
  expression = expression.replace(/MAX\s*\(([^)]+)\)/gi, (match, args) => {
    const values = args.split(',').map((v: string) => evaluateArithmetic(v.trim()));
    return String(Math.max(...values.filter((v: number) => !isNaN(v))));
  });
  
  // MIN(a, b, ...)
  expression = expression.replace(/MIN\s*\(([^)]+)\)/gi, (match, args) => {
    const values = args.split(',').map((v: string) => evaluateArithmetic(v.trim()));
    return String(Math.min(...values.filter((v: number) => !isNaN(v))));
  });
  
  // ABS(x)
  expression = expression.replace(/ABS\s*\(([^)]+)\)/gi, (match, arg) => {
    const value = evaluateArithmetic(arg.trim());
    return String(Math.abs(isNaN(value) ? 0 : value));
  });
  
  return expression;
}

/**
 * Evaluates comparison expressions (<, >, <=, >=, ==, !=)
 */
function evaluateComparison(expression: string): boolean {
  // Handle quoted strings in comparisons
  const stringRegex = /"([^"]+)"/g;
  const stringMap: Record<string, string> = {};
  let stringIndex = 0;
  
  expression = expression.replace(stringRegex, (match, content) => {
    const key = `__STRING_${stringIndex++}__`;
    stringMap[key] = content;
    return key;
  });
  
  // Evaluate comparison
  let result: boolean;
  
  if (expression.includes('<=')) {
    const [left, right] = expression.split('<=').map(s => s.trim());
    const leftVal = parseValue(left, stringMap);
    const rightVal = parseValue(right, stringMap);
    result = leftVal <= rightVal;
  } else if (expression.includes('>=')) {
    const [left, right] = expression.split('>=').map(s => s.trim());
    const leftVal = parseValue(left, stringMap);
    const rightVal = parseValue(right, stringMap);
    result = leftVal >= rightVal;
  } else if (expression.includes('==')) {
    const [left, right] = expression.split('==').map(s => s.trim());
    const leftVal = parseValue(left, stringMap);
    const rightVal = parseValue(right, stringMap);
    result = leftVal === rightVal || String(leftVal) === String(rightVal);
  } else if (expression.includes('!=')) {
    const [left, right] = expression.split('!=').map(s => s.trim());
    const leftVal = parseValue(left, stringMap);
    const rightVal = parseValue(right, stringMap);
    result = leftVal !== rightVal && String(leftVal) !== String(rightVal);
  } else if (expression.includes('<')) {
    const [left, right] = expression.split('<').map(s => s.trim());
    const leftVal = parseValue(left, stringMap);
    const rightVal = parseValue(right, stringMap);
    result = leftVal < rightVal;
  } else if (expression.includes('>')) {
    const [left, right] = expression.split('>').map(s => s.trim());
    const leftVal = parseValue(left, stringMap);
    const rightVal = parseValue(right, stringMap);
    result = leftVal > rightVal;
  } else {
    // No comparison operator found
    return Boolean(evaluateArithmetic(expression));
  }
  
  return result;
}

/**
 * Parses a value, handling strings and numbers
 */
function parseValue(value: string, stringMap: Record<string, string>): any {
  // Check if it's a string placeholder
  if (value.startsWith('__STRING_') && value in stringMap) {
    return stringMap[value];
  }
  
  // Try to parse as number
  const num = parseFloat(value);
  if (!isNaN(num) && value.trim() !== '') {
    return num;
  }
  
  // Return as string (remove quotes if present)
  return value.replace(/^"|"$/g, '');
}

/**
 * Evaluates basic arithmetic expressions
 * Supports: +, -, *, /, %, parentheses
 */
function evaluateArithmetic(expression: string): number {
  // Remove whitespace
  expression = expression.replace(/\s/g, '');
  
  // Handle parentheses first
  while (expression.includes('(')) {
    expression = expression.replace(/\(([^()]+)\)/g, (match, innerExpr) => {
      return String(evaluateArithmetic(innerExpr));
    });
  }
  
  // Evaluate multiplication, division, modulo first
  expression = expression.replace(/(\d+\.?\d*)\s*([*/%])\s*(\d+\.?\d*)/g, (match, left, op, right) => {
    const leftNum = parseFloat(left);
    const rightNum = parseFloat(right);
    if (op === '*') return String(leftNum * rightNum);
    if (op === '/') {
      if (rightNum === 0) throw new Error('Division by zero');
      return String(leftNum / rightNum);
    }
    if (op === '%') return String(leftNum % rightNum);
    return match;
  });
  
  // Evaluate addition and subtraction
  // Handle negative numbers and operations
  expression = expression.replace(/([+-]?\d+\.?\d*)\s*([+-])\s*(\d+\.?\d*)/g, (match, left, op, right) => {
    const leftNum = parseFloat(left);
    const rightNum = parseFloat(right);
    return op === '+' ? String(leftNum + rightNum) : String(leftNum - rightNum);
  });
  
  const result = parseFloat(expression);
  if (isNaN(result)) {
    throw new Error(`Invalid arithmetic expression: ${expression}`);
  }
  
  return result;
}

/**
 * Escapes special regex characters
 */
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

